import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, Microphone } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusinessesContext } from '../../context/BusinessesContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { getAllProducts } from '../../services/businessService';
import VoiceSearchSheet from './VoiceSearchSheet';
import { vozSoportada } from '../../hooks/useVoiceSearch';
import ConfirmModal from '../shared/ConfirmModal';

const VOICE_PERMISSION_KEY = 'voiceSearchPermissionAsked';

// Cache de productos a nivel módulo: se llena la primera vez que se abre el
// overlay y se reutiliza durante el resto de la sesión.
let allProductsCache = null;
let allProductsPromise = null;

const PLACEHOLDERS = ['Hamburguesa', 'Pizza', 'Tacos', 'Combo', 'Bebida', 'Postre'];

const generateSlug = (name) =>
  (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const buildImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

// Normaliza texto para búsqueda: lowercase + sin acentos + trim.
const normalize = (str) =>
  (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

// Diccionario chico de sinónimos / formas alternativas → forma canónica que
// SÍ aparece en el catálogo. Se aplica DESPUÉS de normalizar el query.
const SYNONYMS = {
  pizzeria: 'pizza',
  pizzerias: 'pizza',
  pizzas: 'pizza',
  hamburguesas: 'hamburguesa',
  burger: 'hamburguesa',
  burgers: 'hamburguesa',
  hamburger: 'hamburguesa',
  taqueria: 'taco',
  taquerias: 'taco',
  tacos: 'taco',
  sushis: 'sushi',
  bebidas: 'bebida',
  drinks: 'bebida',
  postres: 'postre',
  desserts: 'postre',
  comidas: 'comida',
  restaurantes: 'restaurante',
  cafe: 'cafe',
  cafes: 'cafe',
  cafeteria: 'cafe',
  cafeterias: 'cafe',
};

// Expande el query del usuario a la forma canónica que matchea con el
// catálogo. Aplica: sinónimos explícitos → fallback de plural→singular.
const expandQuery = (q) => {
  const n = normalize(q);
  if (!n) return '';
  if (SYNONYMS[n]) return SYNONYMS[n];
  // Plural→singular ingenuo: cubre la mayoría de palabras en español.
  if (n.endsWith('es') && n.length > 4) return n.slice(0, -2);
  if (n.endsWith('s') && n.length > 3) return n.slice(0, -1);
  return n;
};

// Resalta en negrita el tramo del nombre que coincide con la query.
function HighlightedText({ text, query }) {
  if (!query) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + query.length);
  const after = text.slice(idx + query.length);
  return (
    <span className="font-normal text-gray-700">
      {before}
      <span className="font-bold text-gray-800">{match}</span>
      {after}
    </span>
  );
}

export default function SearchOverlay({ isOpen, onClose, onProductClick }) {
  const navigate = useNavigate();
  const { businesses } = useBusinessesContext();
  const [query, setQuery] = useState('');
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [allProducts, setAllProducts] = useState(allProductsCache || []);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showVoiceSheet, setShowVoiceSheet] = useState(false);
  const inputRef = useRef(null);

  const voiceSupported = vozSoportada;

  const handleMicClick = (e) => {
    e.stopPropagation();
    const asked = localStorage.getItem(VOICE_PERMISSION_KEY);
    if (asked === 'true') {
      setShowVoiceSheet(true);
    } else {
      setShowPermissionModal(true);
    }
  };

  const handlePermissionAllow = () => {
    localStorage.setItem(VOICE_PERMISSION_KEY, 'true');
    setShowPermissionModal(false);
    setShowVoiceSheet(true);
  };

  // Cuando voz arroja un transcript, lo metemos al input (state + localStorage)
  // y dejamos que el filtro renderice los resultados.
  const handleVoiceResult = (text) => {
    setQuery(text);
    localStorage.setItem('searchQuery', text);
    window.dispatchEvent(new Event('searchUpdated'));
  };

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) {
      // Reset completo al cerrar: limpia state local + localStorage + notifica
      // a SearchBar. Próxima vez que abra (o que voz dispare), arranca limpio
      // (a menos que voz haya escrito justo antes de abrir).
      setQuery('');
      localStorage.setItem('searchQuery', '');
      window.dispatchEvent(new Event('searchUpdated'));
      return;
    }
    // Carga el query actual desde localStorage. Sirve cuando voz lo prellenó
    // justo antes de abrir el overlay. Si está vacío, el input arranca limpio.
    setQuery(localStorage.getItem('searchQuery') || '');
    const t = setTimeout(() => inputRef.current?.focus(), 80);

    // Lazy load: fetch del catálogo completo la primera vez. Reusa cache o
    // promesa en vuelo para evitar fetches duplicados si se abre rápido.
    if (!allProductsCache && !allProductsPromise) {
      setLoadingProducts(true);
      allProductsPromise = getAllProducts()
        .then((data) => {
          allProductsCache = Array.isArray(data) ? data : [];
          setAllProducts(allProductsCache);
        })
        .catch(() => {
          allProductsCache = [];
          setAllProducts([]);
        })
        .finally(() => {
          allProductsPromise = null;
          setLoadingProducts(false);
        });
    } else if (allProductsCache && allProducts.length === 0) {
      setAllProducts(allProductsCache);
    }

    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || query) return;
    const interval = setInterval(() => {
      setPlaceholderIdx((p) => (p + 1) % PLACEHOLDERS.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [isOpen, query]);

  const trimmed = query.trim().toLowerCase();
  // Query "inteligente": acentos quitados + sinónimos + plural→singular.
  const smartQuery = useMemo(() => expandQuery(query), [query]);

  // Mezcla negocios + productos en una sola lista plana. NEGOCIOS PRIMERO
  // (más relevante para el usuario), luego platillos como complemento.
  const results = useMemo(() => {
    if (!smartQuery) return [];
    const list = [];
    const matches = (text) => normalize(text).includes(smartQuery);

    (businesses || []).forEach((b) => {
      if (matches(b.name) || matches(b.category?.name) || matches(b.categoryName)) {
        list.push({
          kind: 'business',
          id: b._id || b.id,
          name: b.name,
          subtitle: 'Negocio',
          image: buildImageUrl(b.logo),
          payload: b,
        });
      }
    });

    (allProducts || []).forEach((p) => {
      if (matches(p.name)) {
        const bizId = p.businessId?._id || p.businessId;
        const biz = businesses?.find((b) => (b._id || b.id) === bizId);
        list.push({
          kind: 'product',
          id: p._id || p.id,
          name: p.name,
          subtitle: 'Platillo',
          image: buildImageUrl(p.image),
          payload: { ...p, businessId: bizId, businessRef: biz, businessName: biz?.name || '' },
        });
      }
    });

    return list.slice(0, 30);
  }, [businesses, allProducts, smartQuery]);

  // Sugerencias para mostrar cuando una búsqueda no tiene resultados — primeros
  // negocios del catálogo para que el usuario nunca quede en pantalla vacía.
  const suggestionItems = useMemo(() => {
    return (businesses || []).slice(0, 6).map((b) => ({
      kind: 'business',
      id: b._id || b.id,
      name: b.name,
      subtitle: 'Negocio',
      image: buildImageUrl(b.logo),
      payload: b,
    }));
  }, [businesses]);

  // Wrapper de cierre: garantiza que el query local + localStorage + SearchBar
  // quedan reseteados ANTES de cerrar (no solo deferido al effect).
  const handleClose = () => {
    setQuery('');
    localStorage.setItem('searchQuery', '');
    window.dispatchEvent(new Event('searchUpdated'));
    onClose?.();
  };

  const handleTap = (item) => {
    handleClose();
    if (item.kind === 'business') {
      const slug = item.payload.slug || generateSlug(item.payload.name);
      navigate(`/business/${slug}`, { state: { business: item.payload } });
      return;
    }
    // Producto: si la pantalla nos pasó callback (Home → abre ProductDetailSheet)
    // lo usamos; si no, navegamos al negocio del producto como fallback razonable.
    if (onProductClick) {
      onProductClick(item.payload, item.payload.businessRef);
    } else {
      const biz = item.payload.businessRef;
      if (biz) {
        const slug = biz.slug || generateSlug(biz.name);
        navigate(`/business/${slug}`, { state: { business: biz } });
      }
    }
  };

  const hasContent = !!trimmed;

  if (typeof document === 'undefined') return null;

  // Portal a body para escapar transform ancestors (slider de MarketLayout).
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop oscuro siempre presente */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/45"
            onClick={handleClose}
          />

          {/* Tarjeta blanca con rounded-b-3xl: crece a medida que aparecen resultados */}
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            layout
            className="fixed top-0 left-0 right-0 z-[101] bg-white rounded-b-3xl shadow-xl flex flex-col"
            style={{ maxHeight: '90dvh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header: back + título centrado */}
            <div className="px-4 pt-[env(safe-area-inset-top)] flex-shrink-0">
              <div className="relative flex items-center justify-center h-14">
                <button
                  onClick={handleClose}
                  className="absolute left-0 w-10 h-10 -ml-2 flex items-center justify-center text-gray-800 active:scale-95 transition-transform"
                  aria-label="Cerrar búsqueda"
                >
                  <ArrowLeft size={22} weight="bold" />
                </button>
                <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">
                  Busca comida rica y al mejor precio
                </h2>
              </div>

              {/* Input con borde brand */}
              <div className={hasContent ? 'pb-2' : 'pb-4'}>
                <div className="relative bg-white border-2 border-brand-primary rounded-full flex items-center pr-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQuery(v);
                      localStorage.setItem('searchQuery', v);
                      window.dispatchEvent(new Event('searchUpdated'));
                    }}
                    className="flex-1 pl-5 pr-2 py-3.5 bg-transparent outline-none text-gray-800 text-[15px] min-w-0"
                  />
                  {!query && (
                    <div className="absolute inset-y-0 left-0 right-12 flex items-center pl-5 pointer-events-none overflow-hidden">
                      <span className="text-gray-500 text-[15px]">Prueba&nbsp;'</span>
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={placeholderIdx}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="text-gray-500 text-[15px]"
                        >
                          {PLACEHOLDERS[placeholderIdx]}
                        </motion.span>
                      </AnimatePresence>
                      <span className="text-gray-500 text-[15px]">'</span>
                    </div>
                  )}
                  {query && (
                    <button
                      onClick={() => {
                        setQuery('');
                        localStorage.setItem('searchQuery', '');
                        window.dispatchEvent(new Event('searchUpdated'));
                      }}
                      className="w-9 h-9 flex items-center justify-center text-gray-500 flex-shrink-0 active:scale-95 transition-transform"
                      aria-label="Limpiar búsqueda"
                    >
                      <X size={18} weight="bold" />
                    </button>
                  )}
                  {voiceSupported && (
                    <>
                      <span className="h-6 w-px bg-gray-300 flex-shrink-0" aria-hidden="true" />
                      <button
                        type="button"
                        onClick={handleMicClick}
                        className="w-9 h-9 ml-1 flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                        aria-label="Búsqueda por voz"
                      >
                        <Microphone size={22} weight="fill" className="text-searchbar-mic-icon" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Resultados: solo se renderizan cuando hay query */}
            {hasContent && (
              <div className="flex-1 overflow-y-auto pb-2">
                {results.length === 0 && loadingProducts && (
                  <div className="px-5 py-10 text-center">
                    <div className="inline-block w-6 h-6 border-2 border-gray-200 border-t-brand-primary rounded-full animate-spin" />
                    <p className="text-xs text-gray-500 mt-3">Buscando...</p>
                  </div>
                )}

                {results.length === 0 && !loadingProducts && suggestionItems.length > 0 && (
                  <>
                    <div className="px-5 pt-5 pb-3">
                      <p className="text-[14px] text-gray-700 leading-snug">
                        No encontramos resultados para "<span className="font-semibold">{query}</span>", te sugerimos:
                      </p>
                    </div>
                    {suggestionItems.map((item) => (
                      <button
                        key={`suggest-${item.id}`}
                        onClick={() => handleTap(item)}
                        className="w-full flex items-center gap-3 px-5 py-3 active:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg">🍽️</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] leading-tight truncate text-gray-700">{item.name}</p>
                          <p className="text-[13px] text-gray-500 mt-0.5">{item.subtitle}</p>
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {results.map((item) => (
                  <button
                    key={`${item.kind}-${item.id}`}
                    onClick={() => handleTap(item)}
                    className="w-full flex items-center gap-3 px-5 py-3 active:bg-gray-50 transition-colors text-left"
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg">🍽️</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] leading-tight truncate">
                        <HighlightedText text={item.name} query={smartQuery} />
                      </p>
                      <p className="text-[13px] text-gray-500 mt-0.5">{item.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Permission modal — primera vez que se usa voz en la sesión.
              zIndex alto para quedar por encima del overlay (z-[101]). */}
          <ConfirmModal
            isOpen={showPermissionModal}
            onClose={() => setShowPermissionModal(false)}
            onConfirm={handlePermissionAllow}
            title="Permitir acceso al micrófono"
            message="Te ayudará a traducir tu voz a una búsqueda dentro de VaChido."
            confirmLabel="Permitir"
            cancelLabel="Denegar"
            zIndex={110}
          />

          {/* Voice sheet — captura la frase y llena el input del overlay.
              zIndex alto para quedar por encima del overlay (z-[101]). */}
          <VoiceSearchSheet
            isOpen={showVoiceSheet}
            onClose={() => setShowVoiceSheet(false)}
            onTranscript={handleVoiceResult}
            onUsarTeclado={() => inputRef.current?.focus()}
            zIndex={110}
          />
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
